import * as React from "react"
import { cn } from "@/lib/utils"
import { motion } from "framer-motion"

export interface SectionHeroProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  description?: string
  gradient?: "primary" | "success" | "warning" | "danger" | "info"
  shimmer?: boolean
  actions?: React.ReactNode
  accessory?: React.ReactNode // Right-side accessory (status/time)
}

const gradientMap = {
  primary: "bg-gradient-to-br from-primary/20 via-primary/10 to-transparent",
  success: "bg-gradient-to-br from-admin-success/20 via-admin-success/10 to-transparent",
  warning: "bg-gradient-to-br from-admin-warning/20 via-admin-warning/10 to-transparent",
  danger: "bg-gradient-to-br from-admin-danger/20 via-admin-danger/10 to-transparent",
  info: "bg-gradient-to-br from-admin-info/20 via-admin-info/10 to-transparent",
}

export function SectionHero({
  title,
  description,
  gradient = "primary",
  shimmer = true,
  actions,
  accessory,
  className,
  ...props
}: SectionHeroProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl p-8 md:p-12",
        gradientMap[gradient],
        className
      )}
      {...props}
    >
      {shimmer && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          animate={{
            x: ["-100%", "100%"],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      )}
      
      <div className="relative z-10 flex items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-admin-hero font-bold text-foreground mb-2">{title}</h1>
          {description && (
            <p className="text-admin-section text-muted-foreground max-w-2xl">{description}</p>
          )}
          {actions && (
            <div className="mt-6 flex flex-wrap gap-3">
              {actions}
            </div>
          )}
        </div>
        {accessory && (
          <div className="shrink-0">
            {accessory}
          </div>
        )}
      </div>
    </div>
  )
}

